// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Skeleton loader component for the asset waterfall chart.
 *
 * Provides a simple, reliable loading state using MUI components.
 */
import { Box, Skeleton, Stack } from '@mui/material';

interface Props {
  height?: number;
}

export function AssetWaterfallChartSkeleton(props: Props) {
  const height = props.height || 400;

  return (
    <Box
      width="100%"
      height={height}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        p: 2,
      }}
    >
      {/* Chart area skeleton */}
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'end',
          justifyContent: 'space-around',
          gap: 1,
          position: 'relative',
        }}
      >
        {/* Waterfall bars */}
        {[120, 80, 200, 60, 40, 100].map((barHeight, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {/* Value label above bar */}
            <Skeleton variant="text" width={20} height={16} />

            {/* Bar */}
            <Skeleton
              variant="rectangular"
              width={160}
              height={barHeight}
              sx={{
                borderRadius: '2px 2px 0 0',
                animationDelay: `${index * 0.1}s`,
              }}
            />

            {/* Label below bar */}
            <Skeleton variant="text" width={60} height={14} />
          </Box>
        ))}
      </Box>

      {/* Y-axis labels */}
      <Stack
        spacing={2}
        sx={{
          position: 'absolute',
          left: 8,
          top: '10%',
          height: '60%',
          justifyContent: 'space-between',
        }}
      >
        {[1, 2, 3, 4, 5].map((tick) => (
          <Skeleton key={tick} variant="text" width={40} height={12} />
        ))}
      </Stack>
    </Box>
  );
}
